import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Car, Bike, Truck } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { compressImageFile } from "@/lib/image";
import { sanitizeFolio } from "@/lib/utils";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { useAutoDismiss } from "@/hooks/useAutoDismiss";
import { useAuth } from "@/context/AuthContext";
import { loadPrinterConfig, buildTicket, buildCorteTicket, buildGastoTicket, buildCortesiaTicket, buildEstacionamientoTicket, horarioDeHoy, printOrFallback, openDrawer } from "@/lib/escposHelper";
import ConfiguracionImpresoraModal from "@/components/ConfiguracionImpresoraModal";
import PointCobroModal from "@/components/PointCobroModal";
import { useTheme } from "@/context/ThemeContext";
import { enqueue, isNetworkError, saveSnapshot, getSnapshot, savePatio, getPatio, patioRemove } from "@/lib/outbox";
import { playSiren, unlockAudio } from "@/lib/alertSound";
import { calcMontoLocal, minutosLocal, estanciaTexto } from "@/domain/tarifa";
import { getAforo, getAutosEnPatio, getInventarioPatio, getTicketsCobrados, buscarTicket, getCatalogoLocal, buscarProductos, getProductoPorCodigo, getTarifas, getTarifasVehiculos, crearTicketIngreso, crearVenta, venderProducto, venderServicio, ventaRapida, crearEvidencia, checkoutTicket, getCajaActiva, abrirCaja, cerrarCaja, crearGasto, nuevaIdemKey } from "@/services/operativaService";
import { getSucursal } from "@/services/sucursalesService";
import { aiParse } from "@/services/aiService";
import { CorteThermalReceipt } from "@/components/terminal/CorteThermalReceipt";
import { SalidaThermalReceipt } from "@/components/terminal/SalidaThermalReceipt";
import { EntryThermalReceipt } from "@/components/terminal/EntryThermalReceipt";
import { AforoIndicator } from "@/components/terminal/AforoIndicator";
import { IngresoVehiculos } from "@/components/terminal/IngresoVehiculos";
import { SalidaCobroPanel } from "@/components/terminal/SalidaCobroPanel";
import { MonitorPatio } from "@/components/terminal/MonitorPatio";
import { ServiciosDirectos } from "@/components/terminal/ServiciosDirectos";
import { VentaRapidaCard } from "@/components/terminal/VentaRapidaCard";
import { UltimosCobros } from "@/components/terminal/UltimosCobros";
import { TerminalHeader } from "@/components/terminal/TerminalHeader";
import { AbrirTurnoScreen } from "@/components/terminal/AbrirTurnoScreen";
import { CheckoutDialog } from "@/components/terminal/CheckoutDialog";
import { PointDialog } from "@/components/terminal/PointDialog";
import { SuccessDialog } from "@/components/terminal/SuccessDialog";
import { HistorialDialog } from "@/components/terminal/HistorialDialog";
import { CobroServicioDialog } from "@/components/terminal/CobroServicioDialog";
import { VentaRapidaDialog } from "@/components/terminal/VentaRapidaDialog";
import { RetailReceiptDialog } from "@/components/terminal/RetailReceiptDialog";
import { RecepcionInventarioDialog } from "@/components/terminal/RecepcionInventarioDialog";
import { GastoDialog } from "@/components/terminal/GastoDialog";
import { GastoThermalReceipt } from "@/components/terminal/GastoThermalReceipt";
import { CortesiaDialog } from "@/components/terminal/CortesiaDialog";
import { CortesiaThermalReceipt } from "@/components/terminal/CortesiaThermalReceipt";
import { CortesiaComprobantesDialog } from "@/components/terminal/CortesiaComprobantesDialog";
import { LogoutBloqueadoDialog } from "@/components/terminal/LogoutBloqueadoDialog";
import { CierreTurnoDialog } from "@/components/terminal/CierreTurnoDialog";
import { fmtDateTime, setActiveTimeZone } from "@/lib/datetime";

const VEHICULOS = [
  { tipo: "Auto", label: "Auto", Icon: Car },
  { tipo: "Moto", label: "Moto", Icon: Bike },
  { tipo: "Camioneta", label: "Camioneta", Icon: Truck },
];

// Sprint 20.0 — icono por tipo de vehículo para el grid dinámico
const ICON_VEHICULO = { Auto: Car, Moto: Bike, Camioneta: Truck };

export default function Terminal() {
  const { user, logout } = useAuth();
  const { sunMode, toggleSunMode } = useTheme();
  // Aplica el Modo Sol también al <body> para que los modales portalizados adopten el estilo.
  useEffect(() => {
    document.body.classList.toggle("sun", sunMode);
    return () => document.body.classList.remove("sun");
  }, [sunMode]);
  // Modo Sol GLOBAL: aplica la clase en <body> para cubrir también los modales (portales de Radix).
  useEffect(() => {
    if (sunMode) document.body.classList.add("sun"); else document.body.classList.remove("sun");
    return () => document.body.classList.remove("sun");
  }, [sunMode]);
  const navigate = useNavigate();
  const [sucursal, setSucursal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caja, setCaja] = useState(null);
  const [fondo, setFondo] = useState("");
  const [opening, setOpening] = useState(false);
  const [aforo, setAforo] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [tarifas, setTarifas] = useState([]);
  // Sprint 20.0 — Grid dinámico: servicios de la sucursal + cobro directo
  const [serviciosSuc, setServiciosSuc] = useState([]);
  // Sprint 20.1 — tipos de vehículo con tarifa (motor v2 + legacy) para el grid de entrada
  const [tarifasVehiculos, setTarifasVehiculos] = useState([]);
  const [servicioCobro, setServicioCobro] = useState(null);
  const [servCant, setServCant] = useState(1);
  const [servMetodo, setServMetodo] = useState("Efectivo");
  const [servPaying, setServPaying] = useState(false);
  const servPayingRef = useRef(false);  // Fase 1 (POS) — guard síncrono anti doble-clic
  const [servReceipt, setServReceipt] = useState(null);
  const [placa, setPlaca] = useState("");
  const [generando, setGenerando] = useState(false);
  // Sprint 30.0 — Tríada de captura de placa en Terminal (voz Web Speech + foto Claude Sonnet)
  const [escuchandoPlaca, setEscuchandoPlaca] = useState(false);
  const [placaAiLoading, setPlacaAiLoading] = useState(false);
  const [vozIaActiva, setVozIaActiva] = useState(false);
  const [fotoIaActiva, setFotoIaActiva] = useState(false);
  const [metodoRegistro, setMetodoRegistro] = useState("MANUAL");
  const recognitionRef = useRef(null);
  // Dictado por voz: tolerancia a silencio (~2.8s) para permitir pausas naturales.
  const dictSilenceRef = useRef(null);
  const dictWantStopRef = useRef(false);
  const dictCommittedRef = useRef("");
  const dictSessionRef = useRef("");
  const placaFileRef = useRef(null);
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [cart, setCart] = useState([]);
  const [coCart, setCoCart] = useState([]); // carrito unificado del checkout (catálogo nuevo)
  const [catOpen, setCatOpen] = useState(false);
  const [catData, setCatData] = useState({ productos: [], servicios: [] });
  const [catLoading, setCatLoading] = useState(false);
  const [scanCode, setScanCode] = useState("");
  const [cobrando, setCobrando] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [efectivo, setEfectivo] = useState("");
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [ultimosCobros, setUltimosCobros] = useState([]);
  const [coOpen, setCoOpen] = useState(false);
  const [coQuery, setCoQuery] = useState("");
  const [coTicket, setCoTicket] = useState(null);
  // Sprint 15.3 — Boleto perdido
  const [esBoletoPerdido, setEsBoletoPerdido] = useState(false);
  const [bpFotos, setBpFotos] = useState([]);
  const [bpUploading, setBpUploading] = useState(false);
  const [penResult, setPenResult] = useState(null);
  const [penProcessing, setPenProcessing] = useState(false);
  const [penIngreso, setPenIngreso] = useState("");
  const [penIngresando, setPenIngresando] = useState(false);
  const [cobroOpen, setCobroOpen] = useState(false);
  const [cobroMonto, setCobroMonto] = useState("");
  const [cobroMetodo, setCobroMetodo] = useState("Efectivo");
  const [coPaying, setCoPaying] = useState(false);
  // Sprint 26.0 — Cortesía (cobro $0 con motivo)
  const [motivosCortesia, setMotivosCortesia] = useState([]);
  const [cortesiaOpen, setCortesiaOpen] = useState(false);
  const [cortesiaMotivo, setCortesiaMotivo] = useState("");
  const [pointOpen, setPointOpen] = useState(false);
  const [pointDevice, setPointDevice] = useState("");
  const [pointDevices, setPointDevices] = useState([]);
  const [pointDevicesLoading, setPointDevicesLoading] = useState(false);
  const [pointStatus, setPointStatus] = useState("idle"); // idle|sending|waiting|approved|error
  const [pointTxId, setPointTxId] = useState(null);
  const [successReceipt, setSuccessReceipt] = useState(null);
  const [pointTech, setPointTech] = useState(null);
  const [pointMsg, setPointMsg] = useState("");
  const [pointOrderStatus, setPointOrderStatus] = useState(null);
  const [comisionCfg, setComisionCfg] = useState(null);
  // Sprint 17.0 — Propinas (solo Tarjeta/Point)
  const [propinasActivas, setPropinasActivas] = useState(false);
  const [cortesiasActivas, setCortesiasActivas] = useState(false); // Sprint 31.1 — gating estricto: oculto hasta confirmar config de sucursal
  const [topePropinaPct, setTopePropinaPct] = useState(100); // Sprint 17.2 — tope configurable por sucursal
  // Sprint 18.0 Etapa B — permisos financieros + gastos + corte
  const [perms, setPerms] = useState({ puede_realizar_corte: false, puede_registrar_gastos: false, requiere_permiso_corte: false, requiere_permiso_gastos: false });
  const [gastoOpen, setGastoOpen] = useState(false);
  const [gastoMonto, setGastoMonto] = useState("");
  const [gastoCategoria, setGastoCategoria] = useState("Insumos");
  const [gastoConcepto, setGastoConcepto] = useState("");
  const [gastoSaving, setGastoSaving] = useState(false);
  // Sprint 29.0 — configurar impresora + candado de salida de turno
  const [printerCfgOpen, setPrinterCfgOpen] = useState(false);
  const [logoutWarnOpen, setLogoutWarnOpen] = useState(false);
  const [corteReceipt, setCorteReceipt] = useState(null);
  const [gastoReceipt, setGastoReceipt] = useState(null);
  const [gastosTurno, setGastosTurno] = useState([]);
  const [gastosLoading, setGastosLoading] = useState(false);
  // Sprint 43.0 — Comprobante de CORTESÍA (impresión + reimpresión del turno)
  const [cortesiaReceipt, setCortesiaReceipt] = useState(null);
  const [cortesiasTurno, setCortesiasTurno] = useState([]);
  const [cortesiasLoading, setCortesiasLoading] = useState(false);
  const [cortesiaComprobOpen, setCortesiaComprobOpen] = useState(false);
  const [propina, setPropina] = useState(0);
  const [propinaCustom, setPropinaCustom] = useState("");
  // Búsqueda dual prominente (Folio / Placa) con foco permanente
  const [salidaQuery, setSalidaQuery] = useState("");
  const salidaInputRef = useRef(null);
  const [paperWidth, setPaperWidth] = useState(() => Number(sessionStorage.getItem("pap_paper_width")) || 80);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMeta, setSuccessMeta] = useState({ metodo: "", total: 0, folio_aut: null });
  const [histOpen, setHistOpen] = useState(false);
  const [histList, setHistList] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [entryReceipt, setEntryReceipt] = useState(null);
  // Sprint Pernoctas — modal de Recepción de Inventario Nocturno al abrir turno
  const [recepcionOpen, setRecepcionOpen] = useState(false);
  const [inventarioPatio, setInventarioPatio] = useState(null);
  // Sprint 27.0 — Monitor de vehículos en patio (estacionamiento general activo) + búsqueda por nombre
  const [patioAutos, setPatioAutos] = useState([]);
  const [patioOpen, setPatioOpen] = useState(true);
  const [patioLoading, setPatioLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [catQuery, setCatQuery] = useState("");
  // Sprint 27.2/27.3 — Venta Rápida (Retail): búsqueda + carrito + cobro en una transacción
  const [retailQuery, setRetailQuery] = useState("");
  const [retailCart, setRetailCart] = useState([]);
  const [retailMetodo, setRetailMetodo] = useState("Efectivo");
  const [retailPaying, setRetailPaying] = useState(false);
  const retailPayingRef = useRef(false);  // Fase 1 (POS) — guard síncrono anti doble-clic
  const [retailReceipt, setRetailReceipt] = useState(null);
  const retailInputRef = useRef(null);
  const [retailModalOpen, setRetailModalOpen] = useState(false); // Venta Rápida en ventana emergente
  const placaInputRef = useRef(null); // foco de retorno al cerrar/cobrar Retail
  // Cobro con tarjeta (Mercado Pago Point) para ventas directas Retail/Servicios.
  const [pointDirecto, setPointDirecto] = useState(null);
  const pollRef = useRef(null);
  const searchRef = useRef(null);
  const audioCtxRef = useRef(null);
  const cajaRef = useRef(null);
  cajaRef.current = caja;
  const [wsStatus, setWsStatus] = useState("connecting");
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);

  const sid = sucursal?.id;

  useEffect(() => {
    if (sid) setPointDevice(sessionStorage.getItem(`pap_point_device_${sid}`) || "");
  }, [sid]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const refreshAforo = useCallback(async (id) => {
    try { const { data } = await getAforo(id); setAforo(data); } catch {}
  }, []);

  // Sprint 15.1 — Reinicio de datos de prueba / purga de aforo (reactivo)
  const resetDemo = async () => {
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/reset-demo-data?id_sucursal=${sid}`);
      toast.success(`Estacionamiento vaciado · ${data.tickets_eliminados} tickets`);
      setResetOpen(false);
      setSuccessReceipt(null);
      setEntryReceipt(null);
      await refreshAforo(sid);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo reiniciar");
    } finally { setResetting(false); }
  };

  const loadUltimosCobros = useCallback(async (sucId, cajaId) => {
    if (!sucId || !cajaId) return;
    try {
      const { data } = await getTicketsCobrados(sucId, { idCaja: cajaId, limit: 8 });
      setUltimosCobros(data);
    } catch { /* silencioso: panel informativo */ }
  }, []);

  const loadAutosPatio = useCallback(async (sucId) => {
    if (!sucId) return;
    setPatioLoading(true);
    try {
      const { data } = await getAutosEnPatio(sucId);
      setPatioAutos(data.autos || []);
      savePatio(sucId, data.autos || []); // Sprint 35.0 — cache para búsqueda/cobro offline
    } catch {
      // Sin red: usa el último patio cacheado para poder cobrar en efectivo offline
      try { const cached = await getPatio(sucId); if (cached) setPatioAutos(cached); } catch {}
    }
    finally { setPatioLoading(false); }
  }, []);

  const boot = useCallback(async () => {
    const raw = localStorage.getItem("pap_sucursal");
    if (!raw) { navigate("/operativo/seleccion", { replace: true }); return; }
    const s = JSON.parse(raw);
    // Sprint 29.0 — sucursal SOLO_VALET: la Terminal está oculta; redirige al tablero Valet
    if (s.modelo_cobro === "SOLO_VALET") { navigate("/operativo/valet", { replace: true }); return; }
    setSucursal(s);
    if (s.zona_horaria) setActiveTimeZone(s.zona_horaria);
    setLoading(true);
    api.get("/mi-empresa").then((r) => setComisionCfg(r.data)).catch(() => {});
    getSucursal(s.id).then((r) => {
      if (r.data?.modelo_cobro === "SOLO_VALET") { navigate("/operativo/valet", { replace: true }); return; }
      if (r.data?.zona_horaria) setActiveTimeZone(r.data.zona_horaria);
      setPropinasActivas(!!r.data?.propinas_activas); setTopePropinaPct(Number(r.data?.tope_propina_pct ?? 100));
      setCortesiasActivas(r.data?.cortesias_activas !== false);
      setVozIaActiva(!!r.data?.voz_ia_activa); setFotoIaActiva(!!r.data?.foto_ia_activa);
    }).catch(() => {});
    api.get(`/operativa/mis-permisos?id_sucursal=${s.id}`).then((r) => setPerms(r.data)).catch(() => {});
    api.get("/operativa/motivos-cortesia").then((r) => setMotivosCortesia(r.data?.motivos || [])).catch(() => {});
    try {
      const { data } = await getCajaActiva(s.id);
      setCaja(data);
      if (data) {
        const [t, cat, tv] = await Promise.all([
          getTarifas(s.id),
          getCatalogoLocal(s.id),
          getTarifasVehiculos(s.id),
        ]);
        setTarifas(t.data);
        setServiciosSuc(cat.data?.servicios || []);
        setCatData(cat.data || { productos: [], servicios: [] });
        setTarifasVehiculos(tv.data || []);
        // Sprint 35.0 — cachea snapshot de tarifas (última tarifa conocida) para cobro offline
        api.get(`/operativa/tarifa-snapshot?id_sucursal=${s.id}`).then((r) => saveSnapshot(s.id, r.data)).catch(() => {});
        refreshAforo(s.id);
        loadUltimosCobros(s.id, data.id_caja);
        loadAutosPatio(s.id);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, [navigate, refreshAforo, loadUltimosCobros, loadAutosPatio]);

  useEffect(() => { boot(); }, [boot]);

  // Sprint 29.0 — candado de salida de turno (cerrar sesión o pestaña con turno con movimientos)
  const turnoConMovimientos = !!caja && ultimosCobros.length > 0;
  const salirOperativo = () => { logout(); navigate("/"); };
  const intentarSalir = () => { if (turnoConMovimientos) setLogoutWarnOpen(true); else salirOperativo(); };
  useEffect(() => {
    const beforeUnload = (e) => {
      if (!(caja && ultimosCobros.length > 0)) return;
      e.preventDefault();
      e.returnValue = "Tienes un turno de caja activo. Realiza el corte antes de salir.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [caja, ultimosCobros.length]);

  // Sprint 27.0 — ticker para el tiempo de estancia en vivo del monitor de patio
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Sprint 10.0 — Sincronización en vivo de la Terminal (tickets y caja), aislada por sucursal
  // Desbloquea el audio (sirena de alertas) tras el primer gesto del usuario (política del navegador).
  useEffect(() => {
    const unlock = () => { unlockAudio(); window.removeEventListener("pointerdown", unlock); };
    window.addEventListener("pointerdown", unlock);
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    if (!sid) return;
    let closedByUs = false;
    const connect = () => {
      setWsStatus("connecting");
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/api/ws/terminal/${sid}`);
      wsRef.current = ws;
      ws.onopen = () => setWsStatus("connected");
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          const { type } = msg;
          if (type === "CAJA_ACTUALIZADA") {
            getCajaActiva(sid).then(({ data }) => {
              setCaja(data);
              refreshAforo(sid);
              if (data) loadUltimosCobros(sid, data.id_caja);
              loadAutosPatio(sid);
            }).catch(() => {});
          } else if (type === "VALET_SOLICITADO") {
            // Sprint 15.2 — cliente pidió su auto desde la landing pública: alerta sonora + aviso
            chime();
            toast(`🔔 Cliente solicitó su auto: ${msg.folio || ""}`, { description: "Preparar entrega en bahía de Valet" });
          } else if (type === "VALET_ALERTA") {
            // Alerta operativa de Valet (cruce de umbral SLA) — visual + sirena de alto impacto
            playSiren();
            toast.error(`🚨 ${msg.titulo || "Alerta de Valet"}`, { description: msg.mensaje || (msg.folio ? `Folio ${msg.folio}` : "") });
          } else { // TICKET_ENTRADA | TICKET_SALIDA
            refreshAforo(sid);
            loadAutosPatio(sid);
            if (cajaRef.current) loadUltimosCobros(sid, cajaRef.current.id_caja);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setWsStatus("disconnected");
        if (!closedByUs) wsReconnectRef.current = setTimeout(connect, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => {
      closedByUs = true;
      clearTimeout(wsReconnectRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [sid, refreshAforo, loadUltimosCobros, loadAutosPatio]);

  const abrirTurno = async () => {
    const val = parseFloat(fondo);
    if (isNaN(val) || val < 0) return toast.error("Ingresa un fondo inicial válido");
    setOpening(true);
    try {
      await abrirCaja({ id_sucursal: sid, fondo_inicial: val });
      toast.success("Turno abierto");
      // Sprint Pernoctas — recepción de inventario nocturno heredado del turno anterior
      try {
        const { data } = await getInventarioPatio(sid);
        if (data?.conteo > 0) { setInventarioPatio(data); setRecepcionOpen(true); }
      } catch { /* noop */ }
      boot();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setOpening(false); }
  };

  const registrarGasto = async () => {
    const monto = parseFloat(gastoMonto);
    if (isNaN(monto) || monto <= 0) return toast.error("Ingresa un monto válido");
    if (!gastoConcepto.trim()) return toast.error("El concepto es obligatorio");
    setGastoSaving(true);
    try {
      const { data: g } = await crearGasto({ id_caja: caja.id_caja, monto, categoria: gastoCategoria, concepto: gastoConcepto.trim() });
      toast.success("Gasto registrado");
      setGastoOpen(false); setGastoMonto(""); setGastoConcepto(""); setGastoCategoria("Insumos");
      if (closeOpen) { try { const { data } = await api.get(`/operativa/turno-resumen?id_caja=${caja.id_caja}`); setResumen(data); } catch { /* noop */ } }
      imprimirGasto(g); // ofrecer impresión del comprobante (best-effort; en nativo abre el diálogo)
      cargarGastosTurno();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGastoSaving(false); }
  };

  const imprimirCorte = (snapshot) => {
    const r = snapshot; if (!r) return;
    const cfg = loadPrinterConfig();
    const brand = comisionCfg?.nombre || sucursal?.nombre || "PARKNOVA";
    const reimpresion = !!snapshot.reimpresion;
    const doNative = () => { setCorteReceipt({ ...r, brand, sucursalNombre: sucursal?.nombre, reimpresion }); printThermal(cfg.papel ? Number(cfg.papel) : paperWidth); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const bytes = buildCorteTicket({ brand, sucursal: sucursal?.nombre, operador: r.operador, resumen: r, efectivoContado: r.efectivoContado, reimpresion, cfg: { ...cfg, abrirCajon: false } });
      printOrFallback(bytes, cfg).then((res) => { if (res === "nativo") doNative(); else toast.success("Corte enviado a la impresora"); }).catch(() => doNative());
    } catch { doNative(); }
  };

  // Sprint 17.3 — Reimprimir el comprobante del último corte cerrado (snapshot, sin alterar datos)
  const reimprimirUltimoCorte = async () => {
    const sid = caja?.id_sucursal || sucursal?.id;
    if (!sid) return;
    try {
      const { data } = await api.get(`/operativa/ultimo-corte?id_sucursal=${sid}`);
      imprimirCorte({ ...data, efectivoContado: data.efectivo_cierre, reimpresion: true });
      toast.success("Reimprimiendo último corte");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No hay cortes para reimprimir");
    }
  };

  // Sprint 42.0 — Comprobante térmico de GASTO / NÓMINA (datos de solo lectura; nunca re-registra).
  const imprimirGasto = (gasto, { reimpresion = false } = {}) => {
    if (!gasto) return;
    const cfg = loadPrinterConfig();
    const brand = comisionCfg?.nombre || sucursal?.nombre || "PARKNOVA";
    const payload = {
      brand, sucursal: sucursal?.nombre,
      operador: gasto.operador || user?.usuario_corto || user?.email || "",
      fecha: gasto.fecha ? fmtDateTime(gasto.fecha) : fmtDateTime(new Date()),
      categoria: gasto.categoria, concepto: gasto.concepto, monto: gasto.monto, reimpresion,
    };
    const doNative = () => { setGastoReceipt(payload); printThermal(cfg.papel ? Number(cfg.papel) : paperWidth); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const bytes = buildGastoTicket({ ...payload, cfg: { ...cfg, abrirCajon: false } });
      printOrFallback(bytes, cfg).then((res) => { if (res === "nativo") doNative(); else toast.success("Comprobante enviado a la impresora"); }).catch(() => doNative());
    } catch { doNative(); }
  };

  const cargarGastosTurno = async () => {
    if (!caja?.id_caja) return;
    setGastosLoading(true);
    try { const { data } = await api.get(`/operativa/gastos?id_caja=${caja.id_caja}`); setGastosTurno(data || []); }
    catch { /* noop */ }
    finally { setGastosLoading(false); }
  };
  useEffect(() => { if (gastoOpen) cargarGastosTurno(); }, [gastoOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sprint 43.0 — Comprobante térmico de CORTESÍA (datos de solo lectura; nunca re-aplica ni mueve caja).
  const imprimirCortesia = (c, { reimpresion = false } = {}) => {
    if (!c) return;
    const cfg = loadPrinterConfig();
    const brand = comisionCfg?.nombre || sucursal?.nombre || "PARKNOVA";
    const payload = {
      brand, sucursal: sucursal?.nombre,
      operador: c.operador || user?.usuario_corto || user?.email || "",
      fecha: c.fecha ? fmtDateTime(c.fecha) : fmtDateTime(new Date()),
      folio: c.folio, motivo: c.motivo, importe: c.importe, reimpresion,
    };
    const doNative = () => { setCortesiaReceipt(payload); printThermal(cfg.papel ? Number(cfg.papel) : paperWidth); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const bytes = buildCortesiaTicket({ ...payload, cfg: { ...cfg, abrirCajon: false } });
      printOrFallback(bytes, cfg).then((res) => { if (res === "nativo") doNative(); else toast.success("Comprobante de cortesía enviado a la impresora"); }).catch(() => doNative());
    } catch { doNative(); }
  };

  const cargarCortesiasTurno = async () => {
    if (!caja?.id_caja) return;
    setCortesiasLoading(true);
    try { const { data } = await api.get(`/operativa/cortesias?id_caja=${caja.id_caja}`); setCortesiasTurno(data || []); }
    catch { /* noop */ }
    finally { setCortesiasLoading(false); }
  };
  const abrirComprobantesCortesia = () => { setCortesiaComprobOpen(true); cargarCortesiasTurno(); };

  const cerrandoRef = useRef(false);
  const [cerrando, setCerrando] = useState(false);
  const cerrarTurno = async () => {
    if (cerrandoRef.current) return; // guard síncrono anti doble-clic (inmune al mismo tick)
    const val = parseFloat(efectivo);
    if (isNaN(val)) return toast.error("Ingresa el efectivo de cierre");
    cerrandoRef.current = true;
    setCerrando(true);
    try {
      await cerrarCaja({ id_caja: caja.id_caja, efectivo_cierre: val });
      imprimirCorte({ ...resumen, efectivoContado: val });
      toast.success("Turno cerrado");
      setCloseOpen(false); setCaja(null); setEfectivo(""); setResumen(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { cerrandoRef.current = false; setCerrando(false); }
  };

  const openCierre = async () => {
    setCloseOpen(true); setResumen(null); setEfectivo(""); setLoadingResumen(true);
    try {
      const { data } = await api.get(`/operativa/turno-resumen?id_caja=${caja.id_caja}`);
      setResumen(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoadingResumen(false); }
  };

  // Sprint 30.0 — Dictado de placa por voz (Web Speech API nativa, sin costo de créditos)
  // Sprint 34.1 (Modo Flash) — interimResults: el texto aparece en el input MIENTRAS se dicta.
  const dictarPlaca = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Tu navegador no soporta dictado por voz");
    if (escuchandoPlaca) { dictWantStopRef.current = true; try { recognitionRef.current?.stop(); } catch {} return; }
    dictWantStopRef.current = false;
    dictCommittedRef.current = "";
    dictSessionRef.current = "";
    const SILENCE_MS = 2800; // tolerancia a silencio continuo (~2.5-3s) antes de finalizar
    const clearSilence = () => { if (dictSilenceRef.current) { clearTimeout(dictSilenceRef.current); dictSilenceRef.current = null; } };
    const startRec = () => {
      const rec = new SR();
      recognitionRef.current = rec;
      rec.lang = "es-MX";
      rec.interimResults = true;
      rec.continuous = true;
      rec.maxAlternatives = 1;
      rec.onstart = () => setEscuchandoPlaca(true);
      rec.onerror = (e) => {
        // 'no-speech'/'aborted' son pausas normales: dejar que onend decida reiniciar.
        if (e.error === "no-speech" || e.error === "aborted") return;
        dictWantStopRef.current = true; clearSilence();
        setEscuchandoPlaca(false); toast.error("No se pudo capturar el audio");
      };
      rec.onresult = (ev) => {
        let texto = "";
        for (let i = 0; i < ev.results.length; i++) texto += ev.results[i][0].transcript;
        dictSessionRef.current = texto;
        const full = `${dictCommittedRef.current} ${texto}`;
        const limpio = full.toUpperCase().replace(/[^A-Z0-9-]/g, "");
        setPlaca(limpio); // actualización en vivo en el input
        setMetodoRegistro("VOZ_IA");
        clearSilence();
        dictSilenceRef.current = setTimeout(() => { dictWantStopRef.current = true; try { rec.stop(); } catch {} }, SILENCE_MS);
      };
      rec.onend = () => {
        clearSilence();
        if (!dictWantStopRef.current) {
          // El navegador cortó durante una pausa breve: conservar lo dicho y seguir escuchando.
          dictCommittedRef.current = `${dictCommittedRef.current} ${dictSessionRef.current}`.trim();
          dictSessionRef.current = "";
          try { startRec(); return; } catch {}
        }
        setEscuchandoPlaca(false);
      };
      rec.start();
    };
    setEscuchandoPlaca(true); // indicador instantáneo (no espera a onstart)
    startRec();
  };

  // Sprint 30.0 — Extracción de placa por foto (Universal Key · Claude Haiku vision) → inyecta solo la placa
  // Sprint 34.1 (Modo Flash) — compresión agresiva (máx 800px) + prompt solo-placa para inferencia ultrarrápida.
  const onPlacaFoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = async () => {
        const max = 800;
        let { width: w, height: h } = img;
        if (w > max || h > max) { const r = Math.min(max / w, max / h); w = Math.round(w * r); h = Math.round(h * r); }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const b64 = canvas.toDataURL("image/jpeg", 0.6);
        setPlacaAiLoading(true);
        try {
          const { data } = await aiParse({ modo: "imagen", imagen_base64: b64, solo_placa: true }, { timeout: 90000 });
          const detectada = (data.placas || "").toUpperCase().trim();
          if (detectada) { setPlaca(detectada); setMetodoRegistro("FOTO_IA"); toast.success(`Placa detectada: ${detectada}`); }
          else toast.error("No se detectó una placa en la foto. Escríbela manualmente.");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "No se pudo analizar la foto");
        } finally { setPlacaAiLoading(false); }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const generarTicket = async (tipo) => {
    setGenerando(true);
    try {
      const body = { id_sucursal: sid, id_caja: caja.id_caja, metodo_registro: metodoRegistro };
      if (tipo) body.tipo_vehiculo = tipo;
      if (placa.trim()) body.placa = placa.trim();
      const { data } = await crearTicketIngreso(body);
      toast.success(`Ticket generado · ${data.folio_impreso}`);
      setPlaca(""); setMetodoRegistro("MANUAL");
      refreshAforo(sid);
      loadAutosPatio(sid);
      // Impresión automática del boleto de entrada con QR (flujo de un solo clic)
      imprimirBoleto({
        sucursal: sucursal?.nombre || data.sucursal_nombre || "",
        folio: data.folio_impreso,
        tipo: tipo || "",
        fecha_entrada: data.fecha_entrada ? new Date(data.fecha_entrada) : new Date(),
        tarifa_texto: data.tarifa_texto || null,
        horario: data.sucursal_horario || null,
        leyenda_corta: data.sucursal_leyenda_corta || null,
        direccion: data.sucursal_direccion || null,
      });
    } catch (err) {
      if (isNetworkError(err)) {
        // Sprint 35.0 — Sin red: guarda el ingreso en la bandeja local (se sincroniza al reconectar).
        const body = { id_sucursal: sid, id_caja: caja.id_caja, metodo_registro: metodoRegistro };
        if (tipo) body.tipo_vehiculo = tipo;
        if (placa.trim()) body.placa = placa.trim();
        await enqueue({ url: "/operativa/tickets", body, kind: "ticket", label: `Ingreso ${tipo || ""} ${placa || ""}`.trim() });
        toast.warning("Sin conexión: ingreso guardado localmente. Se sincronizará al reconectar.");
        setPlaca(""); setMetodoRegistro("MANUAL");
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    }
    finally { setGenerando(false); }
  };

  const buscar = async () => {
    if (!query.trim()) { setResultados([]); return; }
    try {
      const { data } = await buscarProductos(sid, query.trim());
      setResultados(data);
      if (data.length === 1) { addToCart(data[0]); setQuery(""); setResultados([]); }
      else if (data.length === 0) toast.error("Producto no encontrado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addToCart = (p) => {
    if (p.controla_stock && p.stock_actual <= 0) return toast.error("Sin stock disponible");
    setCart((prev) => {
      const ex = prev.find((i) => i.producto.id_producto === p.id_producto);
      if (ex) {
        if (p.controla_stock && ex.cantidad + 1 > p.stock_actual) { toast.error("No hay más stock"); return prev; }
        return prev.map((i) => i.producto.id_producto === p.id_producto ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      return [...prev, { producto: p, cantidad: 1 }];
    });
    setQuery(""); setResultados([]);
    searchRef.current?.focus();
  };

  const setQty = (id, delta) => setCart((prev) => prev
    .map((i) => i.producto.id_producto === id ? { ...i, cantidad: Math.max(1, i.cantidad + delta) } : i));
  const removeItem = (id) => setCart((prev) => prev.filter((i) => i.producto.id_producto !== id));

  const total = cart.reduce((a, i) => a + Number(i.producto.precio) * i.cantidad, 0);

  // ---- Carrito Unificado del checkout (catálogo nuevo: productos + servicios) ----
  const coCartTotal = coCart.reduce((a, i) => a + Number(i.precio) * i.cantidad, 0);

  const coAddItem = (tipo, obj) => {
    const id = tipo === "producto" ? obj.id_producto : obj.id_servicio;
    const key = `${tipo}:${id}`;
    setCoCart((prev) => {
      const ex = prev.find((i) => i.key === key);
      if (ex) {
        if (tipo === "producto" && obj.control_inventario && ex.cantidad + 1 > obj.stock) { toast.error("No hay más stock"); return prev; }
        return prev.map((i) => i.key === key ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      if (tipo === "producto" && obj.control_inventario && obj.stock <= 0) { toast.error("Sin stock disponible"); return prev; }
      return [...prev, {
        key, tipo, id, nombre: obj.nombre || obj.sku_interno || "—",
        precio: Number(obj.precio), cantidad: 1,
        control_inventario: tipo === "producto" ? obj.control_inventario : false,
        stock: tipo === "producto" ? obj.stock : null,
      }];
    });
  };
  const coSetQty = (key, delta) => setCoCart((prev) => prev.map((i) => {
    if (i.key !== key) return i;
    const next = Math.max(1, i.cantidad + delta);
    if (i.tipo === "producto" && i.control_inventario && next > i.stock) { toast.error("No hay más stock"); return i; }
    return { ...i, cantidad: next };
  }));
  const coRemove = (key) => setCoCart((prev) => prev.filter((i) => i.key !== key));

  const abrirCheckout = () => { setCoOpen(true); setCoTicket(null); setCoQuery(""); setCoCart([]); setScanCode(""); setCatOpen(false); setPenResult(null); setCobroOpen(false); };

  const ingresoPension = async () => {
    const q = penIngreso.trim();
    if (!q) return toast.error("Captura la placa o TAG del pensionado");
    setPenIngresando(true);
    try {
      const { data } = await api.post("/operativa/pension-ingreso", { id_sucursal: sid, id_caja: caja?.id_caja, q });
      toast.success(`Acceso autorizado · ${data.cliente} (${data.placa})`);
      setPenIngreso("");
      refreshAforo(sid);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPenIngresando(false); }
  };

  const salidaPension = async () => {
    if (!coTicket) return;
    setPenProcessing(true);
    try {
      const { data } = await api.post("/operativa/pension-salida", { id_ticket: coTicket.id_ticket });
      setPenResult(data);
      if (data.suspendido) { setCobroMonto(String(data.saldo_pendiente)); setCobroMetodo("Efectivo"); setCobroOpen(false); }
      refreshAforo(sid);
      loadUltimosCobros(sid, caja?.id_caja);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPenProcessing(false); }
  };

  const cobrarSaldoPension = async () => {
    const monto = Number(cobroMonto);
    if (!monto || monto <= 0) return toast.error("Captura un monto válido");
    if (!coTicket?.contrato_id) return;
    setCobrando(true);
    try {
      await api.post("/pension/pagos", { id_contrato_pension: coTicket.contrato_id, monto: Number(monto.toFixed(2)), metodo: cobroMetodo, referencia: `Caseta · folio ${coTicket.folio_impreso}` });
      toast.success("Saldo cobrado. Barrera liberada, el vehículo puede salir.");
      setCobroOpen(false); setCoOpen(false); setCoTicket(null); setPenResult(null);
      loadUltimosCobros(sid, caja?.id_caja);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCobrando(false); }
  };

  const loadCatalogo = async () => {
    setCatOpen((v) => !v);
    if (catData.productos.length || catData.servicios.length) return;
    setCatLoading(true);
    try {
      const { data } = await getCatalogoLocal(sid);
      setCatData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCatLoading(false); }
  };

  // Sprint 27.0 — carga el catálogo bajo demanda (para la búsqueda por nombre, sin togglear)
  const fetchCatalogoIfNeeded = async () => {
    if (catData.productos.length || catData.servicios.length || catLoading) return;
    setCatLoading(true);
    try {
      const { data } = await getCatalogoLocal(sid);
      setCatData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCatLoading(false); }
  };

  const scanCheckoutProducto = async () => {
    const code = scanCode.trim();
    if (!code) return;
    try {
      const { data } = await getProductoPorCodigo(sid, code);
      coAddItem("producto", data);
      beep();
      setScanCode("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Motor de comisiones dinámicas (cobro con tarjeta)
  const montoValet = coTicket?.es_valet ? Number(coTicket.monto_valet || 0) : 0;
  const subtotalSalida = coTicket ? Number(coTicket.monto_estacionamiento) + montoValet + coCartTotal : 0;
  // Sprint 15.3 — penalización por boleto perdido (se suma al total)
  const penalizacionBP = esBoletoPerdido ? Number(coTicket?.costo_boleto_perdido || 0) : 0;
  const totalConPenalizacion = Math.round((subtotalSalida + penalizacionBP) * 100) / 100;
  const trasladaComision = !!comisionCfg?.trasladar_comision_tarjeta;
  // Sprint 17.0 — propina (solo tarjeta). El cargo por servicio cubre subtotal + propina.
  const propinaNum = propinasActivas ? Math.max(0, Number(propina) || 0) : 0;
  const baseComisionable = Math.round((subtotalSalida + propinaNum) * 100) / 100;
  const cargoServicio = trasladaComision
    ? Math.round((baseComisionable * (Number(comisionCfg.tasa_comision_porcentaje) / 100) + Number(comisionCfg.comision_cargo_fijo)) * 100) / 100
    : 0;
  const granTotalTarjeta = Math.round((subtotalSalida + propinaNum + cargoServicio) * 100) / 100;
  // Sprint 17.2 — tope de seguridad configurable: la propina no puede exceder el tope % del subtotal
  const topePropinaMonto = Math.round(subtotalSalida * (Number(topePropinaPct) || 100) / 100 * 100) / 100;
  const propinaExcede = propinasActivas && propinaNum > topePropinaMonto + 0.0001;

  const cobrar = async () => {
    if (cart.length === 0) return;
    setCobrando(true);
    try {
      const items = cart.map((i) => ({ id_producto: i.producto.id_producto, cantidad: i.cantidad }));
      const { data } = await crearVenta(sid, { id_caja: caja.id_caja, items });
      toast.success(`Cobrado $${data.total.toFixed(2)} · ${data.lineas} línea(s)`);
      setCart([]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCobrando(false); }
  };

  const hasPorHora = tarifas.some((t) => t.esquema_cobro === "Por_Hora");

  // Sprint 20.1 — Botones de vehículos generados dinámicamente. Fuente autoritativa:
  // /operativa/tarifas-vehiculos (motor v2 + fallback legacy). Si vacío, cae a tarifas legacy locales.
  const vehiculosDinamicos = (() => {
    const list = [];
    const seen = new Set();
    for (const item of tarifasVehiculos) {
      const tv = item.tipo_vehiculo;
      if (!tv || tv === "N/A" || seen.has(tv)) continue;
      seen.add(tv);
      list.push({ tipo: tv, label: tv, Icon: ICON_VEHICULO[tv] || Car, precio: Number(item.precio || 0) });
    }
    if (list.length === 0) {
      for (const t of tarifas) {
        const tv = t.tipo_vehiculo;
        if (!tv || tv === "N/A" || seen.has(tv)) continue;
        seen.add(tv);
        list.push({ tipo: tv, label: tv, Icon: ICON_VEHICULO[tv] || Car, precio: Number(t.monto_base || 0) });
      }
    }
    return list;
  })();

  // Sprint 20.0 — Cobro directo de un servicio suelto (Baños/Lavado, etc.)
  const abrirCobroServicio = (s, tipo = "servicio") => {
    if (!caja) { toast.error("Abre un turno antes de cobrar"); return; }
    setServicioCobro({ ...s, _tipo: tipo });
    setServCant(1);
    setServMetodo("Efectivo");
    setServReceipt(null);
  };

  const servTotal = servicioCobro ? Number(servicioCobro.precio || 0) * servCant : 0;

  const confirmarCobroServicio = async () => {
    if (!servicioCobro || !caja) return;
    const esProducto = servicioCobro._tipo === "producto";
    // Tarjeta: NO registrar como pagada aún. Abrir Mercado Pago Point y esperar aprobación.
    if (servMetodo === "Tarjeta") {
      setPointDirecto({
        kind: esProducto ? "producto" : "servicio",
        monto: Number(servTotal.toFixed(2)),
        referencia: esProducto ? "PRODUCTO" : "SERVICIO",
        titulo: `Cobro ${esProducto ? "producto" : "servicio"} · Tarjeta`,
        productos: esProducto ? [{ id_producto: servicioCobro.id_producto, cantidad: servCant }] : [],
        servicios: esProducto ? [] : [{ id_servicio: servicioCobro.id_servicio, cantidad: servCant }],
        receipt: {
          nombre: servicioCobro.nombre, cantidad: servCant,
          precio: Number(servicioCobro.precio || 0), total: Number(servTotal.toFixed(2)),
        },
      });
      return;
    }
    if (servPayingRef.current) return;  // Fase 1 (POS) — anti doble-clic síncrono (una venta = un folio)
    servPayingRef.current = true;
    setServPaying(true);
    const idem = nuevaIdemKey();  // clave estable de la venta (reutilizada ante reintentos)
    try {
      const payload = esProducto
        ? { id_sucursal: sid, id_producto: servicioCobro.id_producto, id_caja: caja.id_caja, cantidad: servCant, metodo_pago: servMetodo, idempotency_key: idem }
        : { id_sucursal: sid, id_servicio: servicioCobro.id_servicio, id_caja: caja.id_caja, cantidad: servCant, metodo_pago: servMetodo, idempotency_key: idem };
      const { data } = await (esProducto ? venderProducto(payload) : venderServicio(payload));
      // Hardware: el cobro en EFECTIVO dispara la apertura de cajón (si la impresora lo soporta).
      if (servMetodo === "Efectivo") {
        try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) openDrawer(cfg).catch(() => {}); } catch {}
      }
      setServReceipt({
        sucursal: sucursal?.nombre || "",
        nombre: data.nombre,
        cantidad: data.cantidad,
        precio: Number(data.precio_unitario),
        total: Number(data.total),
        metodo: data.metodo_pago,
        fecha: data.fecha ? new Date(data.fecha) : new Date(),
      });
      toast.success(`${esProducto ? "Producto" : "Servicio"} cobrado · $${Number(data.total).toFixed(2)}`);
      if (esProducto) setRetailQuery("");
      if (caja) loadUltimosCobros(sid, caja.id_caja);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setServPaying(false); servPayingRef.current = false; }
  };

  const imprimirComprobanteServicio = async () => {
    const r = servReceipt;
    if (!r) return;
    const cfg = loadPrinterConfig();
    try {
      const lines = [
        "COMPROBANTE DE SERVICIO",
        `Sucursal: ${r.sucursal}`,
        `Servicio: ${r.nombre}`,
        `Cantidad: ${r.cantidad}`,
        `P. Unitario: $${r.precio.toFixed(2)}`,
        `Fecha: ${fmtDateTime(r.fecha)}`,
        `Pago: ${r.metodo}`,
      ];
      const bytes = buildTicket({ brand: comisionCfg?.nombre || r.sucursal || "PARKNOVA", folio: "SERVICIO", qrData: r.nombre, lines, total: r.total, cfg: { ...cfg, abrirCajon: false } });
      const res = await printOrFallback(bytes, cfg);
      if (res !== "nativo") toast.success("Comprobante enviado a la impresora");
      else toast("Impresión nativa no disponible en este dispositivo");
    } catch (e) { toast.error(e?.message || "No se pudo imprimir"); }
  };

  const cerrarCobroServicio = () => { setServicioCobro(null); setServReceipt(null); };

  // Aprobación del cobro con tarjeta (Mercado Pago Point) para ventas directas Retail/Servicios.
  // La venta ya quedó registrada en la caja por el backend (finalize). Aquí sólo mostramos el comprobante.
  const onPointDirectoAprobado = (data) => {
    const cfg = pointDirecto;
    if (!cfg) return;
    const folioAut = data?.folio_autorizacion || data?.order_id || "";
    if (cfg.kind === "retail") {
      const receipt = {
        sucursal: sucursal?.nombre || "",
        items: cfg.receipt.items,
        total: cfg.receipt.total,
        metodo: "Tarjeta (Mercado Pago)",
        fecha: new Date(),
        folio_aut: folioAut,
      };
      setRetailReceipt(receipt);
      setRetailCart([]);
      setRetailQuery("");
      imprimirTicketRetail(receipt);
      setRetailModalOpen(false); // tarjeta aprobada → cerrar modal de Retail
    } else {
      const receipt = {
        sucursal: sucursal?.nombre || "",
        nombre: cfg.receipt.nombre,
        cantidad: cfg.receipt.cantidad,
        precio: cfg.receipt.precio,
        total: cfg.receipt.total,
        metodo: "Tarjeta (Mercado Pago)",
        fecha: new Date(),
      };
      setServReceipt(receipt);
    }
    toast.success(`Cobro aprobado · $${Number(cfg.monto).toFixed(2)}`);
    if (caja) loadUltimosCobros(sid, caja.id_caja);
    reloadCatalogo();
    setPointDirecto(null);
  };

  // ---- Sprint 27.3 — Carrito de Venta Rápida (Retail) ----
  const retailCartTotal = retailCart.reduce((a, i) => a + Number(i.precio) * i.cantidad, 0);
  const retailCartCount = retailCart.reduce((a, i) => a + i.cantidad, 0);

  const retailAdd = (p) => {
    if (!caja) { toast.error("Abre un turno para cobrar productos"); return; }
    setRetailCart((prev) => {
      const ex = prev.find((i) => i.id_producto === p.id_producto);
      if (ex) {
        if (p.control_inventario && ex.cantidad + 1 > p.stock) { toast.error("No hay más stock"); return prev; }
        return prev.map((i) => i.id_producto === p.id_producto ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      if (p.control_inventario && (p.stock ?? 0) <= 0) { toast.error("Sin stock disponible"); return prev; }
      return [...prev, {
        id_producto: p.id_producto, nombre: p.nombre || p.sku_interno || "—",
        precio: Number(p.precio), cantidad: 1,
        control_inventario: !!p.control_inventario, stock: p.stock ?? null,
      }];
    });
    // Limpia SIEMPRE el buscador (incluso sin stock) y devuelve el foco para el siguiente artículo,
    // tanto al escanear como al hacer clic en un producto de la lista.
    setRetailQuery("");
    retailInputRef.current?.focus();
  };
  const retailSetQty = (id, delta) => setRetailCart((prev) => prev.flatMap((i) => {
    if (i.id_producto !== id) return [i];
    const next = i.cantidad + delta;
    if (next <= 0) return [];
    if (i.control_inventario && next > i.stock) { toast.error("No hay más stock"); return [i]; }
    return [{ ...i, cantidad: next }];
  }));
  const retailRemove = (id) => setRetailCart((prev) => prev.filter((i) => i.id_producto !== id));
  const retailClear = () => setRetailCart([]);

  // Enter en la barra retail = escaneo/código: resuelve producto exacto por código/SKU y lo agrega
  const retailResolveScan = () => {
    const q = retailQuery.trim();
    if (!q) return;
    const ql = q.toLowerCase();
    let match = catData.productos.find((p) => (p.codigo_barras || "").toLowerCase() === ql || (p.sku_interno || "").toLowerCase() === ql);
    if (!match && retailProductos.length === 1) match = retailProductos[0];
    if (match) {
      retailAdd(match);
      beep();
      setRetailQuery("");
      retailInputRef.current?.focus();
    } else if (retailProductos.length === 0) {
      toast.error(`Sin producto para "${q}"`);
    }
  };

  const reloadCatalogo = async () => {
    try { const { data } = await getCatalogoLocal(sid); setCatData(data || { productos: [], servicios: [] }); } catch {}
  };

  const imprimirTicketRetail = async (r) => {
    if (!r) return;
    const cfg = loadPrinterConfig();
    try {
      const lines = [
        "VENTA RAPIDA - RETAIL",
        `Sucursal: ${r.sucursal}`,
        `Fecha: ${fmtDateTime(r.fecha)}`,
        "--------------------------------",
        ...r.items.map((it) => `${it.cantidad} x ${it.nombre}  $${(Number(it.precio_unitario) * it.cantidad).toFixed(2)}`),
        "--------------------------------",
        `Pago: ${r.metodo}`,
      ];
      const bytes = buildTicket({ brand: comisionCfg?.nombre || r.sucursal || "PARKNOVA", folio: "RETAIL", qrData: `RETAIL-${r.fecha.getTime()}`, lines, total: r.total, cfg: { ...cfg, abrirCajon: false } });
      const res = await printOrFallback(bytes, cfg);
      if (res !== "nativo") toast.success("Comprobante enviado a la impresora");
    } catch (e) { toast.error(e?.message || "No se pudo imprimir"); }
  };

  // ---- Flujo Venta Rápida en ventana emergente (solo UI/flujo; reutiliza toda la lógica existente) ----
  const focusPlaca = () => setTimeout(() => placaInputRef.current?.focus(), 90);
  // Tras cobrar Retail, el operador debe poder escanear un folio de estacionamiento de inmediato.
  const focusFolio = () => setTimeout(() => salidaInputRef.current?.focus(), 90);
  const abrirVentaRapida = () => {
    if (!caja) { toast.error("Abre un turno para cobrar productos"); return; }
    setRetailModalOpen(true);
  };
  // Cierre del modal SIN venta (ESC / overlay / X): vuelve a Terminal y enfoca Placa.
  const cerrarVentaRapida = (v) => { setRetailModalOpen(v); if (!v) focusPlaca(); };
  // Cierre del comprobante de Retail: vuelve a Terminal y enfoca Folio para escanear el siguiente ticket.
  const cerrarComprobanteRetail = () => { setRetailReceipt(null); focusFolio(); };

  // Al abrir la venta rápida, coloca el cursor en el buscador / escáner.
  useEffect(() => {
    if (!retailModalOpen) return;
    const t = setTimeout(() => retailInputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [retailModalOpen]);

  const retailPagar = async () => {
    if (!caja) { toast.error("Abre un turno para cobrar"); return; }
    if (retailCart.length === 0) { toast.error("El carrito está vacío"); return; }
    // Tarjeta: NO registrar como pagada aún. Abrir el flujo de Mercado Pago Point y esperar aprobación.
    if (retailMetodo === "Tarjeta") {
      const itemsSnap = retailCart.map((i) => ({
        nombre: i.nombre, cantidad: i.cantidad, precio_unitario: Number(i.precio),
      }));
      setPointDirecto({
        kind: "retail",
        monto: Number(retailCartTotal.toFixed(2)),
        referencia: "RETAIL",
        titulo: "Cobro Retail · Tarjeta",
        productos: retailCart.map((i) => ({ id_producto: i.id_producto, cantidad: i.cantidad })),
        servicios: [],
        receipt: { items: itemsSnap, total: Number(retailCartTotal.toFixed(2)) },
      });
      return;
    }
    if (retailPayingRef.current) return;  // Fase 1 (POS) — anti doble-clic síncrono (una venta = un folio)
    retailPayingRef.current = true;
    setRetailPaying(true);
    // Clave idempotente ÚNICA de esta venta: se reutiliza en el intento directo y en el reintento
    // offline (outbox), garantizando que una venta = a lo sumo un folio aun al sincronizar sin red.
    const idem = nuevaIdemKey();
    try {
      const { data } = await ventaRapida({
        id_sucursal: sid, id_caja: caja.id_caja, metodo_pago: retailMetodo,
        items: retailCart.map((i) => ({ id_producto: i.id_producto, cantidad: i.cantidad })),
        idempotency_key: idem,
      });
      if (retailMetodo === "Efectivo") {
        try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) openDrawer(cfg).catch(() => {}); } catch {}
      }
      const receipt = {
        sucursal: sucursal?.nombre || "",
        items: data.items,
        total: Number(data.total),
        metodo: data.metodo_pago,
        fecha: data.fecha ? new Date(data.fecha) : new Date(),
      };
      setRetailReceipt(receipt);
      toast.success(`Venta cobrada · $${Number(data.total).toFixed(2)}`);
      setRetailCart([]);
      setRetailQuery("");
      if (caja) loadUltimosCobros(sid, caja.id_caja);
      reloadCatalogo();
      imprimirTicketRetail(receipt); // impresión automática del comprobante
      setRetailModalOpen(false); // venta OK → cerrar modal (el comprobante devolverá el foco a Placa)
    } catch (err) {
      if (isNetworkError(err)) {
        // Sprint 35.0 — Sin red: guarda la venta rápida en la bandeja local (con la MISMA clave
        // idempotente para que la sincronización posterior NO duplique la venta ni el folio).
        await enqueue({
          url: "/operativa/venta-rapida",
          body: { id_sucursal: sid, id_caja: caja.id_caja, metodo_pago: retailMetodo, items: retailCart.map((i) => ({ id_producto: i.id_producto, cantidad: i.cantidad })), idempotency_key: idem },
          kind: "venta-rapida", label: `Venta rápida ${retailCart.length} art.`,
        });
        toast.warning("Sin conexión: venta guardada localmente. Se sincronizará al reconectar.");
        setRetailCart([]);
        setRetailQuery("");
        setRetailModalOpen(false); // encolada offline = venta completada → cerrar modal
        focusFolio();
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    }
    finally { setRetailPaying(false); retailPayingRef.current = false; }
  };

  // Sprint 35.0 — Cobro OFFLINE: resuelve el folio/placa en el patio cacheado y calcula el monto con reloj local.
  const offlineLookup = async (q) => {
    const term = String(q).trim().toUpperCase();
    const [snap, patio] = await Promise.all([getSnapshot(sid), getPatio(sid)]);
    const found = (patio || []).find((a) => String(a.folio || "").toUpperCase() === term || String(a.placa || "").toUpperCase() === term);
    if (!found) { toast.error("Sin conexión: folio no encontrado en el patio local"); return null; }
    const mins = minutosLocal(found.fecha_entrada);
    const monto = calcMontoLocal(snap, found.tipo_vehiculo, mins);
    return {
      __offline: true,
      id_ticket: found.id_ticket,
      folio_impreso: found.folio,
      placa: found.placa || "",
      tipo_vehiculo: found.tipo_vehiculo || "",
      fecha_entrada: found.fecha_entrada,
      minutos_estancia: mins,
      estancia_texto: estanciaTexto(mins),
      monto_estacionamiento: monto,
      es_valet: false,
      monto_valet: 0,
      costo_boleto_perdido: snap?.costo_boleto_perdido || 0,
      es_pernocta: false,
      monto_pernocta: 0,
    };
  };

  // Ejecuta el cobro en efectivo sin red: encola el checkout (respetando folio/monto/hora local) y muestra el éxito.
  const cobrarOffline = async (metodo) => {
    if (!coTicket) return;
    setCoPaying(true);
    try {
      const est = Number(coTicket.monto_estacionamiento || 0);
      const val = Number(coTicket.monto_valet || 0);
      const total = Number((est + val).toFixed(2));
      const salidaISO = new Date().toISOString();
      await enqueue({
        url: "/operativa/checkout",
        body: {
          id_ticket: coTicket.id_ticket, metodo_pago: metodo, id_caja: caja.id_caja,
          offline: true, offline_salida: salidaISO,
          offline_monto_estacionamiento: est, offline_monto_valet: val, offline_total: total,
        },
        kind: "checkout", label: `Cobro offline ${coTicket.folio_impreso} · $${total.toFixed(2)}`,
        folio_local: coTicket.folio_impreso,
      });
      // Retira el auto del patio local (evita doble cobro sin red)
      try { await patioRemove(sid, coTicket.id_ticket); } catch {}
      setPatioAutos((prev) => prev.filter((a) => a.id_ticket !== coTicket.id_ticket));
      setSuccessReceipt({
        sucursal: sucursal?.nombre || "",
        folio: coTicket.folio_impreso,
        placa: coTicket.placa || "",
        tipo: coTicket.tipo_vehiculo || "",
        estancia: coTicket.estancia_texto || "",
        estacionamiento: est, productos: 0, valet: val, cargo: 0, penalizacion: 0,
        es_boleto_perdido: false, total, metodo: "EFECTIVO (OFFLINE)", folio_aut: null, fecha_salida: new Date(),
      });
      setSuccessMeta({ metodo: "Efectivo", total, folio_aut: null });
      toast.warning(`Cobro OFFLINE $${total.toFixed(2)} · folio ${coTicket.folio_impreso}. Se sincronizará al reconectar.`);
      setCoOpen(false);
      setSuccessOpen(true);
      try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) await openDrawer(cfg); } catch {}
    } catch (e) {
      toast.error("No se pudo registrar el cobro offline");
    } finally { setCoPaying(false); }
  };

  const buscarTicketSalida = async () => {
    const q = sanitizeFolio(coQuery);
    if (!q) return;
    try {
      const { data } = await buscarTicket(sid, q);
      setCoTicket(data);
      setEsBoletoPerdido(false);
      setBpFotos([]);
    } catch (err) {
      if (isNetworkError(err)) {
        const off = await offlineLookup(q);
        if (off) { setCoTicket(off); setEsBoletoPerdido(false); setBpFotos([]); toast.warning(`Sin conexión: cobro offline · $${Number(off.monto_estacionamiento).toFixed(2)}`); }
        else setCoTicket(null);
      } else { setCoTicket(null); toast.error(formatApiError(err.response?.data?.detail)); }
    }
  };

  // Sprint 15.3 — Boleto perdido: evidencia OBLIGATORIA (acreditación de propiedad). Captura con cámara + compresión.
  const loadBpFotos = async (idTicket) => {
    try { const { data } = await api.get(`/operativa/evidencias?id_ticket=${idTicket}`); setBpFotos((data || []).filter((f) => f.tipo_evidencia === "Acreditacion")); } catch {}
  };
  const capturarEvidenciaBP = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !coTicket) return;
    setBpUploading(true);
    try {
      const dataUrl = await compressImageFile(file, { maxSize: 1000, quality: 0.7 });
      await crearEvidencia({ id_ticket: coTicket.id_ticket, tipo_evidencia: "Acreditacion", url_imagen: dataUrl });
      toast.success("Evidencia de acreditación guardada");
      loadBpFotos(coTicket.id_ticket);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No se pudo guardar la evidencia"); }
    finally { setBpUploading(false); }
  };

  // Beep corto (Web Audio API) — confirmación auditiva de lectura de escáner. No bloquea la UI.
  const beep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = 1180;
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.11);
      osc.stop(ctx.currentTime + 0.12);
    } catch { /* silencioso: nunca bloquea el cobro */ }
  };

  // Chime de notificación (dos tonos) — llamada de cliente entrante (auto solicitado desde landing).
  const chime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const tono = (freq, start, dur) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        o.start(ctx.currentTime + start);
        o.stop(ctx.currentTime + start + dur);
      };
      tono(784, 0, 0.22);
      tono(1046, 0.18, 0.35);
    } catch { /* silencioso */ }
  };

  // Lectura del escáner físico: abre el cobro y busca el folio automáticamente (hands-free)
  const dispararEscaneo = async (folio) => {
    const f = sanitizeFolio(folio); // normaliza comillas/acentos del lector USB a guiones
    if (!f) return;
    beep(); // confirmación auditiva de lectura válida
    setCoOpen(true);
    setCoQuery(f);
    setCoTicket(null);
    setCoCart([]); setScanCode(""); setCatOpen(false); setCatQuery(""); setPenResult(null);
    try {
      const { data } = await buscarTicket(sid, f);
      setCoTicket(data);
      toast.success(`Ticket ${data.folio_impreso || f} leído`);
    } catch (err) {
      if (isNetworkError(err)) {
        const off = await offlineLookup(f);
        if (off) { setCoTicket(off); toast.warning(`Sin conexión: cobro offline · ${off.folio_impreso}`); }
        else setCoTicket(null);
      } else { setCoTicket(null); toast.error(formatApiError(err.response?.data?.detail)); }
    }
  };

  // Escáner USB unificado (hook compartido con Valet): solo activo con turno abierto.
  useBarcodeScanner({ onScan: dispararEscaneo, enabled: !!caja });

  // Búsqueda dual (Folio o Placa) desde el campo prominente. El backend (ticket-buscar)
  // resuelve por folio O placa activa en una sola consulta.
  const iniciarSalida = () => {
    const f = sanitizeFolio(salidaQuery);
    if (!f) { toast.error("Escanea un folio o escribe una placa"); salidaInputRef.current?.focus(); return; }
    setSalidaQuery("");
    dispararEscaneo(f);
  };

  // Foco permanente en el campo de salida: al abrir turno y al cerrar el modal de cobro.
  useEffect(() => { if (caja) salidaInputRef.current?.focus(); }, [caja]);
  useEffect(() => {
    if (!coOpen && caja) {
      const t = setTimeout(() => salidaInputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [coOpen, caja]);

  // Recupera el foco al hacer clic en zonas vacías (no roba foco de otros inputs/botones).
  const handlePanelClick = (e) => {
    if (!caja) return;
    const t = e.target;
    if (t?.closest && t.closest('input,button,textarea,select,a,[role="button"],[role="switch"]')) return;
    salidaInputRef.current?.focus();
  };

  // Sprint 26.0 — Aplicar cortesía en la caseta (cobro $0 con motivo). Requiere permiso especial.
  const aplicarCortesiaTerminal = async () => {
    if (!coTicket) return;
    if (!cortesiaMotivo) { toast.error("Selecciona un motivo de cortesía"); return; }
    setCoPaying(true);
    try {
      const productos_vendidos = coCart.filter((i) => i.tipo === "producto").map((i) => ({ id_producto: i.id, cantidad: i.cantidad }));
      const servicios_adicionales_vendidos = coCart.filter((i) => i.tipo === "servicio").map((i) => ({ id_servicio: i.id, cantidad: i.cantidad }));
      const { data } = await checkoutTicket({ id_ticket: coTicket.id_ticket, metodo_pago: "Efectivo", id_caja: caja.id_caja, productos_vendidos, servicios_adicionales_vendidos, es_cortesia: true, motivo_cortesia: cortesiaMotivo });
      toast.success(`Cortesía aplicada ($0.00) · ${cortesiaMotivo} · folio ${data.folio}`);
      setCortesiaOpen(false); setCortesiaMotivo(""); setCoOpen(false);
      // Auto-impresión best-effort (la cortesía ya quedó registrada; si falla, reimprimible desde el menú).
      try { imprimirCortesia({ folio: data.folio, motivo: cortesiaMotivo, importe: data.monto_cortesia_otorgado, operador: user?.usuario_corto || user?.email, fecha: new Date() }); } catch { /* noop */ }
      const sid = sucursal?.id;
      if (sid) { refreshAforo(sid); loadUltimosCobros(sid, caja.id_caja); loadAutosPatio(sid); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCoPaying(false); }
  };

  const pagarSalida = async (metodo) => {
    if (!coTicket) return;
    if (esBoletoPerdido && bpFotos.length === 0) {
      toast.error("Captura al menos una foto de acreditación de propiedad antes de cobrar el boleto perdido.");
      return;
    }
    // Sprint 35.0 — ticket resuelto sin red: cobro offline directo (solo efectivo).
    if (coTicket.__offline) { await cobrarOffline("Efectivo"); return; }
    setCoPaying(true);
    try {
      const productos_vendidos = coCart.filter((i) => i.tipo === "producto").map((i) => ({ id_producto: i.id, cantidad: i.cantidad }));
      const servicios_adicionales_vendidos = coCart.filter((i) => i.tipo === "servicio").map((i) => ({ id_servicio: i.id, cantidad: i.cantidad }));
      const { data } = await checkoutTicket({ id_ticket: coTicket.id_ticket, metodo_pago: metodo, id_caja: caja.id_caja, productos_vendidos, servicios_adicionales_vendidos, es_boleto_perdido: esBoletoPerdido });
      toast.success(`Cobrado $${Number(data.gran_total).toFixed(2)} (${metodo}) · folio ${data.folio}`);
      // Comprobante + pantalla de "Cobro Exitoso" (flujo de alta intensidad)
      setSuccessReceipt({
        sucursal: sucursal?.nombre || "",
        folio: data.folio || coTicket.folio_impreso,
        placa: coTicket.placa || "",
        tipo: coTicket.tipo_vehiculo || "",
        estancia: coTicket.estancia_texto || "",
        estacionamiento: Number(data.monto_estacionamiento || 0),
        productos: Number(data.total_productos || 0),
        valet: Number(data.monto_valet || 0),
        cargo: 0,
        penalizacion: Number(data.monto_penalizacion_boleto || 0),
        es_boleto_perdido: !!data.es_boleto_perdido,
        total: Number(data.gran_total || 0),
        metodo: metodo === "Efectivo" ? "EFECTIVO" : String(metodo).toUpperCase(),
        folio_aut: null,
        fecha_salida: new Date(),
      });
      setSuccessMeta({ metodo: metodo === "Efectivo" ? "Efectivo" : metodo, total: Number(data.gran_total || 0), folio_aut: null });
      setCoOpen(false);
      setSuccessOpen(true);
      // Sprint 14.1 — apertura de cajón en todo pago exitoso (si la preferencia está activa)
      try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) await openDrawer(cfg); } catch {}
      refreshAforo(sid);
      loadUltimosCobros(sid, caja.id_caja);
    } catch (err) {
      if (isNetworkError(err)) { await cobrarOffline("Efectivo"); }
      else toast.error(formatApiError(err.response?.data?.detail));
    }
    finally { setCoPaying(false); }
  };

  // Cierra la pantalla de éxito y deja la caseta lista para el siguiente auto (hands-free)
  const finalizarCobro = () => {
    stopPoll();
    setSuccessOpen(false);
    setPointOpen(false);
    setCoOpen(false);
    setCoTicket(null);
    setCoQuery("");
    setCart([]);
    setPointStatus("idle");
    setPointTxId(null);
    setPointTech(null);
    setPointMsg("");
    setPointOrderStatus(null);
    setPropina(0);
    setPropinaCustom("");
    if (sid) refreshAforo(sid);
  };

  // ---------- Cobro con terminal física Mercado Pago Point (Orders API) ----------
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const abrirPagoTarjeta = () => { stopPoll(); setPropina(0); setPropinaCustom(""); setPointStatus("idle"); setPointTxId(null); setPointTech(null); setPointMsg(""); setPointOrderStatus(null); setPointOpen(true); cargarDevices(); };

  const cargarDevices = async () => {
    setPointDevicesLoading(true);
    try {
      const { data } = await api.get(`/mercadopago/point/devices?id_sucursal=${sid}`);
      const list = data.devices || [];
      setPointDevices(list);
      const saved = sessionStorage.getItem(`pap_point_device_${sid}`) || "";
      if (saved && !list.some((d) => d.id === saved)) setPointDevice("");
      if (!saved && list.length === 1) setPointDevice(list[0].id);
    } catch (err) {
      setPointDevices([]);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setPointDevicesLoading(false); }
  };

  const elegirDevice = (id) => {
    setPointDevice(id);
    sessionStorage.setItem(`pap_point_device_${sid}`, id);
  };

  const aplicarEstado = (data) => {
    // Sección técnica
    setPointTech({
      endpoint: data.endpoint, http_status: data.http_status, order_id: data.order_id,
      order_status: data.order_status, status_detail: data.status_detail,
      terminal_id: data.terminal_id, date_created: data.date_created,
    });
    setPointOrderStatus(data.order_status || null);
    setPointMsg(data.mensaje || "");

    if (data.estado === "approved") {
      stopPoll();
      const folioAut = data.folio_autorizacion || data.order_id || "—";
      setSuccessReceipt({
        sucursal: sucursal?.nombre || "",
        folio: coTicket?.folio_impreso || "",
        placa: coTicket?.placa || "",
        tipo: coTicket?.tipo_vehiculo || "",
        estancia: coTicket?.estancia_texto || "",
        estacionamiento: Number(coTicket?.monto_estacionamiento || 0),
        productos: coCartTotal,
        valet: montoValet,
        propina: propinaNum,
        cargo: cargoServicio,
        total: granTotalTarjeta,
        metodo: "TARJETA (MERCADO PAGO)",
        folio_aut: folioAut,
        fecha_salida: new Date(),
      });
      setSuccessMeta({ metodo: "Tarjeta (Mercado Pago)", total: granTotalTarjeta, folio_aut: folioAut });
      setPointStatus("idle");
      setPointOpen(false);
      setSuccessOpen(true);
      // Sprint 14.1 — apertura de cajón en pago con tarjeta aprobado (si la preferencia está activa)
      try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) openDrawer(cfg).catch(() => {}); } catch {}
      refreshAforo(sid);
      loadUltimosCobros(sid, caja?.id_caja);
    } else if (data.estado === "expired") {
      stopPoll(); setPointStatus("error"); setPointMsg(data.mensaje || "La orden expiró. Genera un nuevo cobro.");
    } else if (["rejected", "canceled"].includes(data.estado)) {
      stopPoll(); setPointStatus("error");
    }
    // created | at_terminal | action_required | pending -> seguimos esperando
  };

  const consultarEstado = async (txId) => {
    try {
      const { data } = await api.get(`/mercadopago/point/estado?id_transaccion=${txId}`);
      aplicarEstado(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    }
  };

  const iniciarPolling = (txId) => {
    stopPoll();
    const inicio = Date.now();
    pollRef.current = setInterval(async () => {
      // WCS-43402 — consultar cada 2s durante la vigencia (5 min); al vencer, última consulta obligatoria
      if (Date.now() - inicio > 300000) {
        stopPoll();
        const d = await consultarEstado(txId); // consulta final obligatoria -> actualiza BD con estado real
        const est = d?.estado;
        if (est === "approved" || est === "rejected" || est === "canceled" || est === "expired") return; // aplicarEstado ya lo manejó
        setPointStatus("timeout");
        return;
      }
      await consultarEstado(txId);
    }, 2000);
  };

  const enviarCobroPoint = async () => {
    const device = pointDevice.trim();
    if (!device) return toast.error("Selecciona la terminal");
    if (propinaExcede) return toast.error(`La propina no puede exceder el ${Number(topePropinaPct) || 100}% del subtotal (máx. $${topePropinaMonto.toFixed(2)}).`);
    sessionStorage.setItem(`pap_point_device_${sid}`, device);
    const monto = subtotalSalida;
    setPointStatus("sending"); setPointMsg(""); setPointOrderStatus(null);
    try {
      const productos_vendidos = coCart.filter((i) => i.tipo === "producto").map((i) => ({ id_producto: i.id, cantidad: i.cantidad }));
      const servicios_adicionales_vendidos = coCart.filter((i) => i.tipo === "servicio").map((i) => ({ id_servicio: i.id, cantidad: i.cantidad }));
      const { data } = await api.post("/mercadopago/point/cobrar", {
        id_sucursal: sid, id_ticket: coTicket.id_ticket, id_caja: caja.id_caja, monto, propina: propinaNum, id_device: device, productos_vendidos, servicios_adicionales_vendidos,
      }, { timeout: 60000 });
      setPointTxId(data.id_transaccion);
      setPointTech({ endpoint: "POST /v1/orders", http_status: data.http_status, order_id: data.order_id,
        order_status: data.order_status, status_detail: null, terminal_id: data.terminal_id, date_created: null });
      setPointOrderStatus(data.order_status || "created");
      if (data.duplicada) toast.info(data.mensaje || "Ya existe una orden pendiente para este folio.");
      setPointStatus("waiting");
      iniciarPolling(data.id_transaccion);
    } catch (err) {
      // (req #2) detener animación y mostrar el error real de Mercado Pago
      setPointStatus("error");
      setPointMsg(formatApiError(err.response?.data?.detail));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const consultarManual = async () => {
    if (!pointTxId) return;
    const data = await consultarEstado(pointTxId);
    if (data && ["created", "at_terminal", "action_required"].includes(data.estado)) {
      setPointStatus("waiting"); iniciarPolling(pointTxId);
    }
  };

  const cancelarPendiente = async () => {
    if (!pointTxId) return;
    try {
      const { data } = await api.post(`/mercadopago/point/cancelar?id_transaccion=${pointTxId}`);
      toast[data.ok ? "success" : "warning"](data.mensaje || "");
      stopPoll();
      if (data.ok && data.estado === "canceled") {
        // Solo se confirma cancelado si Mercado Pago lo confirmó
        setPointStatus("error"); setPointMsg(data.mensaje || "Cobro cancelado y confirmado por Mercado Pago.");
      } else if (data.estado === "expired") {
        setPointStatus("error"); setPointMsg(data.mensaje || "La orden expiró; se registró como Expirada.");
      } else if (data.estado === "processed") {
        consultarEstado(pointTxId);
      } else if (data.estado === "at_terminal") {
        setPointStatus("waiting"); setPointMsg(data.mensaje || ""); iniciarPolling(pointTxId);
      } else {
        // MP no confirmó la cancelación: mantener estado real
        setPointStatus("waiting"); setPointMsg(data.mensaje || ""); iniciarPolling(pointTxId);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const printThermal = (width) => {
    const w = Number(width) || paperWidth || 80;
    setPaperWidth(w);
    sessionStorage.setItem("pap_paper_width", String(w));
    // Ajusta dinámicamente el tamaño de página al ancho del rollo térmico (58 / 80 mm)
    let tag = document.getElementById("thermal-page-size");
    if (!tag) { tag = document.createElement("style"); tag.id = "thermal-page-size"; document.head.appendChild(tag); }
    tag.textContent = `@page { size: ${w}mm auto; margin: 0; } .thermal-receipt { width: ${w}mm !important; }`;
    setTimeout(() => {
      // iOS Safari captura la impresión desde la posición de scroll; el comprobante
      // (position:absolute; top:0) queda fuera de vista si la página está scrolleada
      // (p.ej. al reimprimir desde "Autos en patio" / "Últimos cobros") → hoja en blanco.
      // Volver al tope garantiza que el comprobante entre en el área impresa. No cambia el diseño.
      try { window.scrollTo(0, 0); } catch { /* noop */ }
      window.print();
    }, 120);
  };

  // Comprobante de cobro (salida) — MANUAL: sólo al hacer clic. Respeta la config del dispositivo.
  const imprimirComprobante = async (width) => {
    const cfg = loadPrinterConfig();
    const doNative = () => { setEntryReceipt(null); printThermal(width); };
    if (cfg.modo === "nativo") { doNative(); return; }
    const r = successReceipt;
    if (!r) { doNative(); return; }
    try {
      const lines = [
        `Sucursal: ${r.sucursal}`,
        `Folio: ${r.folio}`,
        r.placa ? `Placa: ${r.placa}` : null,
        r.tipo ? `Vehiculo: ${r.tipo}` : null,
        `Salida: ${fmtDateTime(r.fecha_salida)}`,
        r.estancia ? `Tiempo: ${r.estancia}` : null,
        `Estacionamiento: $${Number(r.estacionamiento || 0).toFixed(2)}`,
        r.valet > 0 ? `Valet: $${r.valet.toFixed(2)}` : null,
        r.productos > 0 ? `Productos: $${r.productos.toFixed(2)}` : null,
        r.penalizacion > 0 ? `Boleto perdido (penalizacion): $${r.penalizacion.toFixed(2)}` : null,
        r.propina > 0 ? `Propina (no facturable): $${r.propina.toFixed(2)}` : null,
        r.cargo > 0 ? `Cargo x servicio: $${r.cargo.toFixed(2)}` : null,
        `Pago: ${r.metodo || "—"}`,
        r.folio_aut ? `Autorizacion: ${r.folio_aut}` : null,
      ].filter(Boolean);
      const bytes = buildTicket({ brand: comisionCfg?.nombre || r.sucursal || "PARKNOVA", folio: r.folio, qrData: r.folio, lines, total: r.total, cfg: { ...cfg, abrirCajon: false } });
      const res = await printOrFallback(bytes, cfg);
      if (res === "nativo") { doNative(); return; } // fallback silencioso (USB no disponible)
      toast.success("Comprobante enviado a la impresora");
    } catch (e) { toast.error(e?.message || "No se pudo imprimir"); }
  };

  // Boleto de entrada con QR — AUTOMÁTICO al generar el ticket. Respeta la config del dispositivo.
  const imprimirBoleto = async (data) => {
    const cfg = loadPrinterConfig();
    const qrUrl = `${window.location.origin}/e/${data.folio}`;
    const horarioHoy = horarioDeHoy(data.horario);
    const enriched = { ...data, qrUrl, horarioHoy };
    const doNative = () => { setSuccessReceipt(null); setEntryReceipt(enriched); printThermal(cfg.papel ? Number(cfg.papel) : paperWidth); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const bytes = buildEstacionamientoTicket({
        brand: data.sucursal || sucursal?.nombre || "PARKNOVA",
        folio: data.folio,
        fecha: fmtDateTime(data.fecha_entrada || new Date()),
        tarifaTexto: data.tarifa_texto || null,
        horarioHoy,
        leyenda: data.leyenda_corta || null,
        qrUrl,
        direccionCorta: data.direccion || comisionCfg?.direccion || null,
        cfg: { ...cfg, abrirCajon: false },
      });
      const res = await printOrFallback(bytes, cfg);
      if (res === "nativo") { doNative(); return; } // fallback silencioso (USB no disponible)
    } catch (e) { toast.error(e?.message || "No se pudo imprimir el boleto"); }
  };

  // ---------- Historial / Reimpresión de comprobantes ----------
  const abrirHistorial = async () => {
    setHistOpen(true);
    setHistLoading(true);
    try {
      const { data } = await getTicketsCobrados(sid, { limit: 30 });
      setHistList(data || []);
    } catch (err) { setHistList([]); toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setHistLoading(false); }
  };

  const reimprimirTicket = (t, width) => {
    setSuccessReceipt({
      sucursal: sucursal?.nombre || "",
      folio: t.folio_impreso,
      placa: t.placa || "",
      tipo: t.tipo_vehiculo || "",
      estancia: t.estancia_texto || "",
      estacionamiento: Number(t.monto_estacionamiento || 0),
      productos: Number(t.total_productos || 0),
      valet: Number(t.monto_valet || 0),
      cargo: 0,
      penalizacion: Number(t.monto_penalizacion_boleto || 0),
      propina: Number(t.monto_propina || 0),
      es_boleto_perdido: !!t.es_boleto_perdido,
      total: Number(t.total_pagado || 0),
      metodo: (t.metodo_pago || "").toString().toUpperCase() || "—",
      folio_aut: t.folio_autorizacion || null,
      fecha_salida: t.fecha_salida ? new Date(t.fecha_salida) : new Date(),
    });
    imprimirComprobante(width || paperWidth);
  };

  // Reimpresión del BOLETO DE INGRESO desde "Autos en Patio" (reusa el flujo existente, sin crear tickets/movimientos)
  const reimprimirIngreso = (a) => {
    imprimirBoleto({
      sucursal: sucursal?.nombre || comisionCfg?.nombre || "",
      folio: a.folio,
      tipo: a.tipo_vehiculo || "",
      fecha_entrada: a.fecha_entrada ? new Date(a.fecha_entrada) : new Date(),
      tarifa_texto: a.tarifa_texto || null,
      horario: a.sucursal_horario || null,
      leyenda_corta: comisionCfg?.leyenda_corta || null,
      direccion: comisionCfg?.direccion || null,
    });
  };

  const cerrarPagoPoint = () => {
    // NO cancela automáticamente: si hay orden pendiente se conserva para consultarla luego
    stopPoll();
    setPointOpen(false);
    if (pointStatus !== "waiting" && pointStatus !== "timeout") {
      setPointStatus("idle"); setPointTxId(null); setPointTech(null); setPointOrderStatus(null);
    }
  };


  // Cierre automático (5s) de las ventanas de COBRO EXITOSO ya validado (no aplica a pagos en proceso).
  // Closures lazy para evitar TDZ (los handlers se definen arriba y solo se invocan a los 5s).
  useAutoDismiss(successOpen, () => finalizarCobro());
  useAutoDismiss(!!servReceipt, () => cerrarCobroServicio());
  useAutoDismiss(!!retailReceipt, cerrarComprobanteRetail);

  // ---------- Loading ----------
  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#FFCC00]" /></div>;
  }

  // ---------- Header (shared) ----------
  const headerEl = (
    <TerminalHeader
      sucursal={sucursal} user={user} caja={caja} perms={perms}
      sunMode={sunMode} toggleSunMode={toggleSunMode} navigate={navigate}
      setPrinterCfgOpen={setPrinterCfgOpen} reimprimirUltimoCorte={reimprimirUltimoCorte}
      openCierre={openCierre} setGastoOpen={setGastoOpen} abrirComprobantesCortesia={abrirComprobantesCortesia} intentarSalir={intentarSalir}
      resetOpen={resetOpen} setResetOpen={setResetOpen} aforo={aforo} resetDemo={resetDemo} resetting={resetting} />
  );

  // Comprobante térmico de CORTE — definido antes del early-return para que siga
  // montado en el DOM aunque caja pase a null (fix P0: window.print corre 120ms después).
  const corteThermalBlock = <CorteThermalReceipt corte={corteReceipt} paperWidth={paperWidth} />;

  // ---------- Caja Guard ----------
  if (!caja) {
    return (
      <div className={`min-h-screen bg-[#0A0A0A] text-white flex flex-col overflow-x-hidden ${sunMode ? "sun" : ""}`} data-testid="terminal-root" data-sun-mode={sunMode ? "1" : "0"}>
        {headerEl}
        <AbrirTurnoScreen fondo={fondo} setFondo={setFondo} abrirTurno={abrirTurno} opening={opening} />
        {corteThermalBlock}
      </div>
    );
  }

  const lleno = aforo?.lleno;

  // Sprint 27.0 — tiempo de estancia en vivo (deriva de fecha_entrada + nowTick)
  const minutosEstancia = (fechaEntrada) => Math.max(0, Math.floor((nowTick - new Date(fechaEntrada).getTime()) / 60000));
  const fmtEstancia = (fechaEntrada) => {
    const mins = minutosEstancia(fechaEntrada);
    const h = Math.floor(mins / 60), m = mins % 60;
    return h ? `${h}h ${m}m` : `${m}m`;
  };
  // Filtro por nombre del catálogo (búsqueda por nombre en el carrito de salida)
  const catQ = catQuery.trim().toLowerCase();
  const catProductosFiltrados = catQ
    ? catData.productos.filter((p) => `${p.nombre || ""} ${p.sku_interno || ""} ${p.codigo_barras || ""}`.toLowerCase().includes(catQ))
    : catData.productos;
  const catServiciosFiltrados = catQ
    ? catData.servicios.filter((s) => `${s.nombre || ""} ${s.categoria || ""}`.toLowerCase().includes(catQ))
    : catData.servicios;
  // Sprint 27.2 — Venta Rápida (Retail): productos filtrados por nombre o código
  const retailQ = retailQuery.trim().toLowerCase();
  const retailProductos = retailQ
    ? catData.productos.filter((p) => `${p.nombre || ""} ${p.sku_interno || ""} ${p.codigo_barras || ""}`.toLowerCase().includes(retailQ))
    : catData.productos;

  // ---------- Dashboard Operativo ----------
  return (
    <div className={`min-h-screen bg-[#0A0A0A] text-white flex flex-col overflow-x-hidden ${sunMode ? "sun" : ""}`} data-testid="terminal-root" data-sun-mode={sunMode ? "1" : "0"}>
      {headerEl}

      {/* Aforo */}
      <AforoIndicator aforo={aforo} lleno={lleno} />

      {/* Panel principal — Dashboard 360° (control de patio + ventas directas) */}
      <div className="flex-1 p-5 lg:p-8" onClick={handlePanelClick}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* ===== FILA SUPERIOR — Control de Patio ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Col 1 — Ingreso de vehículos */}
          <IngresoVehiculos
            fotoIaActiva={fotoIaActiva} vozIaActiva={vozIaActiva}
            placaFileRef={placaFileRef} placaInputRef={placaInputRef}
            placa={placa} setPlaca={setPlaca} onPlacaFoto={onPlacaFoto} dictarPlaca={dictarPlaca}
            placaAiLoading={placaAiLoading} escuchandoPlaca={escuchandoPlaca}
            vehiculosDinamicos={vehiculosDinamicos} vehiculosLegacy={VEHICULOS} hasPorHora={hasPorHora}
            generarTicket={generarTicket} generando={generando} lleno={lleno} />
          {/* Col 2 — Salida / Cobro de vehículo */}
          <SalidaCobroPanel
            salidaInputRef={salidaInputRef} salidaQuery={salidaQuery} setSalidaQuery={setSalidaQuery}
            iniciarSalida={iniciarSalida} penIngreso={penIngreso} setPenIngreso={setPenIngreso}
            ingresoPension={ingresoPension} penIngresando={penIngresando} abrirHistorial={abrirHistorial} />
          {/* Col 3 — Monitor de Vehículos en Patio */}
          <MonitorPatio
            patioOpen={patioOpen} setPatioOpen={setPatioOpen} patioAutos={patioAutos} patioLoading={patioLoading}
            minutosEstancia={minutosEstancia} fmtEstancia={fmtEstancia}
            dispararEscaneo={dispararEscaneo} reimprimirIngreso={reimprimirIngreso} />
          </div>

          {/* ===== FILA INFERIOR — Ventas Directas ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Col 1 — Servicios · Cobro directo */}
            <ServiciosDirectos serviciosSuc={serviciosSuc} abrirCobroServicio={abrirCobroServicio} caja={caja} />

            {/* Col 2 — Venta Rápida (Retail) con carrito */}
            <VentaRapidaCard abrirVentaRapida={abrirVentaRapida} caja={caja} />
          </div>

          {/* Últimos cobros del turno — visibilidad en vivo (ancho completo) */}
          <UltimosCobros ultimosCobros={ultimosCobros} reimprimirTicket={reimprimirTicket} paperWidth={paperWidth} />
        </div>
      </div>

      {/* Salida / Checkout dialog */}
      <CheckoutDialog
        coOpen={coOpen} setCoOpen={setCoOpen} coQuery={coQuery} setCoQuery={setCoQuery}
        buscarTicketSalida={buscarTicketSalida} coTicket={coTicket} setCoTicket={setCoTicket}
        penResult={penResult} setPenResult={setPenResult} penProcessing={penProcessing} salidaPension={salidaPension}
        cobroOpen={cobroOpen} setCobroOpen={setCobroOpen} cobroMonto={cobroMonto} setCobroMonto={setCobroMonto}
        cobroMetodo={cobroMetodo} setCobroMetodo={setCobroMetodo} cobrarSaldoPension={cobrarSaldoPension} cobrando={cobrando}
        esBoletoPerdido={esBoletoPerdido} setEsBoletoPerdido={setEsBoletoPerdido} loadBpFotos={loadBpFotos}
        penalizacionBP={penalizacionBP} bpUploading={bpUploading} capturarEvidenciaBP={capturarEvidenciaBP} bpFotos={bpFotos}
        montoValet={montoValet} totalConPenalizacion={totalConPenalizacion} pagarSalida={pagarSalida} coPaying={coPaying}
        abrirPagoTarjeta={abrirPagoTarjeta} cortesiasActivas={cortesiasActivas} perms={perms}
        setCortesiaMotivo={setCortesiaMotivo} setCortesiaOpen={setCortesiaOpen} />

      {/* Cobro con terminal Mercado Pago Point */}
      <PointDialog
        pointOpen={pointOpen} cerrarPagoPoint={cerrarPagoPoint} coTicket={coTicket} subtotalSalida={subtotalSalida}
        pointStatus={pointStatus} propinasActivas={propinasActivas} topePropinaMonto={topePropinaMonto}
        propina={propina} setPropina={setPropina} propinaCustom={propinaCustom} setPropinaCustom={setPropinaCustom}
        propinaExcede={propinaExcede} topePropinaPct={topePropinaPct} propinaNum={propinaNum}
        trasladaComision={trasladaComision} cargoServicio={cargoServicio} granTotalTarjeta={granTotalTarjeta}
        pointDevicesLoading={pointDevicesLoading} pointDevices={pointDevices} cargarDevices={cargarDevices}
        pointDevice={pointDevice} elegirDevice={elegirDevice} enviarCobroPoint={enviarCobroPoint}
        pointMsg={pointMsg} pointOrderStatus={pointOrderStatus} pointTech={pointTech}
        consultarManual={consultarManual} cancelarPendiente={cancelarPendiente}
        setPointStatus={setPointStatus} setPointTxId={setPointTxId} setPointTech={setPointTech}
        setPointOrderStatus={setPointOrderStatus} setPointMsg={setPointMsg} />

      {/* Pantalla de "Cobro Exitoso" — unificada para Efectivo y Tarjeta (flujo de alta intensidad) */}
      <SuccessDialog
        successOpen={successOpen} finalizarCobro={finalizarCobro} successMeta={successMeta}
        paperWidth={paperWidth} setPaperWidth={setPaperWidth} imprimirComprobante={imprimirComprobante} />

      {/* Historial / Reimpresión de comprobantes */}
      <HistorialDialog
        histOpen={histOpen} setHistOpen={setHistOpen} histLoading={histLoading} histList={histList}
        paperWidth={paperWidth} setPaperWidth={setPaperWidth} reimprimirTicket={reimprimirTicket} />

      {/* Sprint 20.0 — Cobro directo de servicio (Baños/Lavado, etc.) */}
      <CobroServicioDialog
        servicioCobro={servicioCobro} servReceipt={servReceipt} cerrarCobroServicio={cerrarCobroServicio}
        servCant={servCant} setServCant={setServCant} servMetodo={servMetodo} setServMetodo={setServMetodo}
        servTotal={servTotal} confirmarCobroServicio={confirmarCobroServicio} servPaying={servPaying}
        imprimirComprobanteServicio={imprimirComprobanteServicio} />

      {/* Venta Rápida · Retail — ventana emergente (reutiliza toda la lógica existente) */}
      <VentaRapidaDialog
        retailModalOpen={retailModalOpen} cerrarVentaRapida={cerrarVentaRapida} retailInputRef={retailInputRef}
        retailQuery={retailQuery} setRetailQuery={setRetailQuery} retailResolveScan={retailResolveScan}
        catData={catData} retailProductos={retailProductos} retailAdd={retailAdd} caja={caja}
        retailCart={retailCart} retailCartCount={retailCartCount} retailCartTotal={retailCartTotal}
        retailClear={retailClear} retailSetQty={retailSetQty} retailRemove={retailRemove}
        retailMetodo={retailMetodo} setRetailMetodo={setRetailMetodo} retailPagar={retailPagar} retailPaying={retailPaying} />

      {/* Sprint 27.3 — Comprobante de Venta Rápida (Retail) con reimpresión */}
      <RetailReceiptDialog
        retailReceipt={retailReceipt} cerrarComprobanteRetail={cerrarComprobanteRetail}
        imprimirTicketRetail={imprimirTicketRetail} />



      {/* Cobro con tarjeta (Mercado Pago Point) para ventas directas Retail/Servicios */}
      {pointDirecto && (
        <PointCobroModal
          open={!!pointDirecto}
          onClose={() => setPointDirecto(null)}
          idSucursal={sid}
          idCaja={caja?.id_caja}
          monto={pointDirecto.monto}
          referencia={pointDirecto.referencia}
          titulo={pointDirecto.titulo}
          productosVendidos={pointDirecto.productos}
          serviciosVendidos={pointDirecto.servicios}
          onApproved={onPointDirectoAprobado}
        />
      )}

      {/* Sprint Pernoctas — Recepción de Inventario Nocturno al abrir turno */}
      <RecepcionInventarioDialog
        recepcionOpen={recepcionOpen} setRecepcionOpen={setRecepcionOpen} inventarioPatio={inventarioPatio} />

      {/* Cerrar turno dialog */}
      <CierreTurnoDialog
        closeOpen={closeOpen} setCloseOpen={setCloseOpen} loadingResumen={loadingResumen} resumen={resumen}
        efectivo={efectivo} setEfectivo={setEfectivo} cerrarTurno={cerrarTurno} cerrando={cerrando} />

      {/* Comprobante térmico de CORTE DE CAJA — oculto en pantalla, visible solo al imprimir */}
      {corteThermalBlock}

      {/* Modal — Registrar Gasto / Nómina + comprobantes del turno */}
      <GastoDialog
        gastoOpen={gastoOpen} setGastoOpen={setGastoOpen} gastoMonto={gastoMonto} setGastoMonto={setGastoMonto}
        gastoCategoria={gastoCategoria} setGastoCategoria={setGastoCategoria} gastoConcepto={gastoConcepto}
        setGastoConcepto={setGastoConcepto} registrarGasto={registrarGasto} gastoSaving={gastoSaving}
        gastosTurno={gastosTurno} gastosLoading={gastosLoading}
        onReimprimirGasto={(g) => imprimirGasto(g, { reimpresion: true })} />

      {/* Comprobante térmico de GASTO — oculto en pantalla, visible solo al imprimir */}
      <GastoThermalReceipt gasto={gastoReceipt} paperWidth={paperWidth} />

      {/* Comprobante térmico — oculto en pantalla, visible solo al imprimir */}
      <SalidaThermalReceipt receipt={successReceipt} comisionCfg={comisionCfg} paperWidth={paperWidth} />

      {/* Boleto de estacionamiento con QR — oculto en pantalla, visible solo al imprimir */}
      <EntryThermalReceipt receipt={entryReceipt} comisionCfg={comisionCfg} paperWidth={paperWidth} />

      {/* Sprint 26.0 — Selección de motivo de cortesía (caseta) */}
      <CortesiaDialog
        cortesiaOpen={cortesiaOpen} setCortesiaOpen={setCortesiaOpen} cortesiaMotivo={cortesiaMotivo}
        setCortesiaMotivo={setCortesiaMotivo} motivosCortesia={motivosCortesia}
        aplicarCortesiaTerminal={aplicarCortesiaTerminal} coPaying={coPaying} />

      {/* Sprint 43.0 — Comprobantes de cortesía del turno (reimpresión) + recibo térmico oculto */}
      <CortesiaComprobantesDialog
        open={cortesiaComprobOpen} onClose={() => setCortesiaComprobOpen(false)}
        cortesias={cortesiasTurno} loading={cortesiasLoading}
        onReimprimir={(c) => imprimirCortesia(c, { reimpresion: true })} />
      <CortesiaThermalReceipt cortesia={cortesiaReceipt} paperWidth={paperWidth} />

      {/* Sprint 29.0 — Configurar impresora (mismo modal que Valet) */}
      <ConfiguracionImpresoraModal open={printerCfgOpen} onClose={() => setPrinterCfgOpen(false)} />

      {/* Sprint 32.0 — Candado de salida: BLOQUEA el cierre de sesión si hay turno con movimientos (sin bypass) */}
      <LogoutBloqueadoDialog logoutWarnOpen={logoutWarnOpen} setLogoutWarnOpen={setLogoutWarnOpen} />
    </div>
  );
}
